import React, { useMemo } from "react";
import { useQuery } from "react-query";
import { colors2, getVal } from "oolib";
import { UI_CAPTION } from "oolib/dist/v2/components/Typo2";

import TableElem from "@/components/cards/TableElem";
import { getDataQueryFn } from "@/utils/react-query-hooks/getData";
import { WidgetEmptyState, WidgetErrorState, WidgetLoadingState } from "../../../../WidgetRenderer/comps/altStatePlaceholders";
import { useUpdateLoadedWidgetsTracker } from "../../../../../ReportLandingContext/useUpdateLoadedWidgetsTracker";
import { DATASETS } from "../madSettings";
import { ControlsRow } from "./ControlsRow";
import { useRoster } from "./queries";

/**
 * "Not started" — the children with NO doc at all in the template, with
 * their volunteer and what the feed knows about the class: the partner (the
 * school, MAD's word for the centre), class, section and slot. There is no doc to
 * build these rows from, so:
 *
 *   everyone in MAD's allocation feed (the whole roster, one call, shared
 *   across the three tabs)
 *     minus
 *   everyone with at least one published doc in the template.
 *
 * The second half is one query asking for the child id of every doc and
 * nothing else — a few bytes a doc. It cannot be paged: "has no doc" is only
 * knowable once every doc has been seen.
 *
 * Filters that are about the volunteer (Volunteer Name, the MAD Role /
 * Centre / Chapter / City / State / Country rollups) narrow the roster — the
 * endpoint translates them to volunteers. The other filters are about the
 * docs; the feed carries nothing they could match, so they leave this list
 * alone.
 *
 * The whole list is in hand at once (nothing is paged), so every column
 * sorts on the frontend — TableElem's own column sort, switched on per
 * column by `sortConfig`. Children come alphabetical by default.
 */
const textCol = ({ colId, headerDisplay, props }) => ({
  colId,
  headerDisplay,
  comp: "TableText",
  valuePath: colId,
  sortConfig: { fn: "alphabetical", path: colId },
  ...(props ? { props } : {}),
});

const TABLE_CONFIG = {
  rowIdPath: "rowId",
  compactHeader: true,
  colWidths: { child: 200, volunteer: 180, partner: 240, class: 90, section: 110, slot: 160 },
  cellData: [
    {
      ...textCol({ colId: "child", headerDisplay: "Child", props: { typoComp: "UI_BODY_SEMIBOLD_SM", color: colors2.grey80 } }),
      defaultSort: true,
    },
    textCol({ colId: "volunteer", headerDisplay: "Volunteer" }),
    textCol({ colId: "partner", headerDisplay: "Partner" }),
    textCol({ colId: "class", headerDisplay: "Class" }),
    textCol({ colId: "section", headerDisplay: "Section" }),
    textCol({ colId: "slot", headerDisplay: "Slot" }),
  ],
};

/** The id of every child with at least one published doc in the template. */
const useAssessedChildIds = (contentType) => {
  const dataset = DATASETS[contentType];
  return useQuery(
    ["madAssessedChildIds", contentType],
    () =>
      getDataQueryFn({
        contentTypes: [contentType],
        findQuery: { kp_published_status: "published" },
        projection: { [dataset.childValuePath]: 1 },
      }).then((res) => new Set((res.data || []).map((doc) => String(getVal(doc, dataset.childValuePath))))),
    { staleTime: 60 * 1000 }
  );
};

export const NotStartedChildren = ({ id, widgetIdx, contentType, filters, controls }) => {
  const roster = useRoster(filters);
  const assessed = useAssessedChildIds(contentType);

  const rows = useMemo(() => {
    if (!roster.data || !assessed.data) return [];
    const assessedIds = assessed.data;
    const seen = new Set();
    const out = [];
    for (const allocation of roster.data) {
      const childId = String(allocation.value);
      if (assessedIds.has(childId)) continue;
      // a child allocated to two volunteers is one row per volunteer
      const rowId = `${childId}|${allocation.volunteer?.madId}`;
      if (seen.has(rowId)) continue;
      seen.add(rowId);
      out.push({
        rowId,
        childId,
        child: allocation.display,
        volunteer: allocation.volunteer?.display || "",
        partner: allocation.partner || "",
        class: allocation.class || "",
        section: allocation.section || "",
        slot: allocation.slot || "",
      });
    }
    return out;
  }, [roster.data, assessed.data]);

  const status =
    roster.status === "error" || assessed.status === "error"
      ? "error"
      : roster.status === "success" && assessed.status === "success"
      ? "success"
      : "loading";

  useUpdateLoadedWidgetsTracker({ status, widgetIdx, hasData: rows.length > 0, widgetId: id });

  const childCount = useMemo(() => new Set(rows.map((r) => r.childId)).size, [rows]);

  return (
    <>
      <ControlsRow controls={controls}>
        {status === "success" && (
          <UI_CAPTION style={{ color: colors2.grey60 }}>
            {childCount} {childCount === 1 ? "child" : "children"}
          </UI_CAPTION>
        )}
      </ControlsRow>
      {status === "loading" ? (
        <WidgetLoadingState />
      ) : status === "error" ? (
        <WidgetErrorState error={roster.error || assessed.error} />
      ) : rows.length === 0 ? (
        <WidgetEmptyState display="Every child has started" />
      ) : (
        <TableElem id={`${id}:${contentType}:notStarted`} status="success" data={rows} tableConfig={TABLE_CONFIG} />
      )}
    </>
  );
};
